import { Injectable } from '@angular/core';
import { IAppDashboard, IPluginPermissions, IUser, PluginInjectorWidget, PluginInjectorService } from 'core-client/api'
import { IMapWidgetPermissions } from '../../interfaces/map-widget-permission.interface'

@Injectable({
  providedIn: 'root'
})
export class MapWidgetPermissionsService {
  private static _widgetPermissions: IMapWidgetPermissions = {
    mapEnabled: false,
    mapSettingsEditEnabled: false,
    wmsChartLoaderEnabled: false,
    ngsildConnectorEnabled: false,
    aisTargetsDataEnabled: false,
    aisPredictionDataEnabled: false,
    maritimeTargetsDataEnabled: false,
    plannedPathDataEnabled: false,
    radarTargetDataEnabled: false,
    fusionMaritimeTargetDataEnabled: false
  };

  static get widgetPermissions(): IMapWidgetPermissions {
    return this._widgetPermissions;
  }

  static async getUserAccessRights(
      user: IUser,
      pluginInjector: PluginInjectorService,
      data: PluginInjectorWidget.IWidget
  ): Promise<IMapWidgetPermissions> {
    const appDashboard = user.app_dashboards.find(
        (dashboard: IAppDashboard) => dashboard._id === user.active_dashboard
    );

    if (appDashboard) {
      const pluginPermissions = await pluginInjector.permissionService.getPermissions(
          appDashboard,
          user
      );

      const accessRights = pluginPermissions.find(
          (plugin_permission: IPluginPermissions) => plugin_permission.plugin_id === data.pluginId
      );

      if (accessRights) {
        MapWidgetPermissionsService.setWidgetPermissions(accessRights, data);
      }
    }

    return this._widgetPermissions;
  }

  private static setWidgetPermissions(accessRights: IPluginPermissions, data: PluginInjectorWidget.IWidget) {
    if (accessRights?.widget_permissions) {
      const mapEnabled =
          data?.widgetPermissions?.widgetEnabledPermission?.permissionRef;
      const settingsEnabled =
          data?.widgetPermissions?.widgetBasePermissions?.widgetSettingsEnabledPermission?.permissionRef;
      const wmsChartLoaderPermission =
          data?.widgetPermissions?.widgetAdditionalPermissions?.['authorizeWmsChartLoader']?.permissionRef;
      const ngsildConnectorPermission =
          data?.widgetPermissions?.widgetAdditionalPermissions?.['authorizeNgsildConnector']?.permissionRef;
      const aisTargetPermission =
          data?.widgetPermissions?.widgetAdditionalPermissions?.['enableAisTargetsData']?.permissionRef;
      const aisPredictionPermission =
          data?.widgetPermissions?.widgetAdditionalPermissions?.['enableAisPredictionData']?.permissionRef;
      const maritimeTargetsDataPermission =
          data?.widgetPermissions?.widgetAdditionalPermissions?.['enableMaritimeTargetsData']?.permissionRef;
      const plannedPathPermission =
          data?.widgetPermissions?.widgetAdditionalPermissions?.['enablePlannedPathData']?.permissionRef;
      const radarTargetPermission =
          data?.widgetPermissions?.widgetAdditionalPermissions?.['enableRadarTargetData']?.permissionRef;
      const fusionMaritimeTargetPermission =
          data?.widgetPermissions?.widgetAdditionalPermissions?.['enableFusionMaritimeTargetData']?.permissionRef;

      if (mapEnabled) {
        this._widgetPermissions.mapEnabled =
            accessRights.widget_permissions[mapEnabled] as boolean;
      }

      if (settingsEnabled) {
        this._widgetPermissions.mapSettingsEditEnabled =
            accessRights.widget_permissions[settingsEnabled] as boolean;
      }

      if (wmsChartLoaderPermission) {
        this._widgetPermissions.wmsChartLoaderEnabled =
            accessRights.widget_permissions[wmsChartLoaderPermission] as boolean;
      }

      if (ngsildConnectorPermission) {
        this._widgetPermissions.ngsildConnectorEnabled =
            accessRights.widget_permissions[ngsildConnectorPermission] as boolean;
      }

      if (aisTargetPermission) {
        this._widgetPermissions.aisTargetsDataEnabled =
            accessRights.widget_permissions[aisTargetPermission] as boolean;
      }

      if (aisPredictionPermission) {
        this._widgetPermissions.aisPredictionDataEnabled =
            accessRights.widget_permissions[aisPredictionPermission] as boolean;
      }

      if (maritimeTargetsDataPermission) {
        this._widgetPermissions.maritimeTargetsDataEnabled =
            accessRights.widget_permissions[maritimeTargetsDataPermission] as boolean;
      }

      if (plannedPathPermission) {
        this._widgetPermissions.plannedPathDataEnabled =
            accessRights.widget_permissions[plannedPathPermission] as boolean;
      }

      if (radarTargetPermission) {
        this._widgetPermissions.radarTargetDataEnabled =
            accessRights.widget_permissions[radarTargetPermission] as boolean;
      }

      if (fusionMaritimeTargetPermission) {
        this._widgetPermissions.fusionMaritimeTargetDataEnabled =
            accessRights.widget_permissions[fusionMaritimeTargetPermission] as boolean;
      }
    }
  }
}
